/**
 * /equipment/reports/cost-by-job — Equipment cost rolled up per project +
 * cost code + equipment. Optionally includes unposted entries (flagged).
 *
 * Output is grouped Project → Cost code → Equipment with subtotals at
 * each level. CSV export available.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useQuery } from "@tanstack/react-query";

import {
  equipmentCostByJobReportQueryOptions,
} from "@/lib/queries";
import type { CostByJobRow } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { ProjectIdSelect } from "@/components/ProjectIdSelect";

export const Route = createFileRoute(
  "/_auth/equipment/reports/cost-by-job",
)({
  component: CostByJobReportPage,
});

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

function CostByJobReportPage() {
  const { enabled, loading: packLoading } = useConstructionPack();
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeUnposted, setIncludeUnposted] = useState(false);

  const reportQ = useQuery(
    equipmentCostByJobReportQueryOptions({
      project_id: projectId || undefined,
      from: from || undefined,
      to: to || undefined,
      include_unposted: includeUnposted,
    }),
  );
  const rows: CostByJobRow[] = reportQ.data?.rows ?? [];

  // Group by project → cost code.
  type Group = { label: string; rows: CostByJobRow[]; subtotal: number };
  type ProjGroup = {
    projectKey: string;
    projectLabel: string;
    codeGroups: Group[];
    total: number;
  };

  const grouped: ProjGroup[] = useMemo(() => {
    const byProj = new Map<string, ProjGroup>();
    for (const r of rows) {
      const projKey = r.project_id ?? "_none";
      const projLabel = r.project_code ?? "(no project)";
      let pg = byProj.get(projKey);
      if (!pg) {
        pg = {
          projectKey: projKey,
          projectLabel: projLabel,
          codeGroups: [],
          total: 0,
        };
        byProj.set(projKey, pg);
      }
      const codeKey = r.cost_code_id ?? "_none";
      let cg = pg.codeGroups.find((g) => g.label === codeKey);
      if (!cg) {
        cg = {
          label: codeKey,
          rows: [],
          subtotal: 0,
        };
        pg.codeGroups.push(cg);
      }
      cg.rows.push(r);
      const t = Number(r.total ?? 0);
      cg.subtotal += t;
      pg.total += t;
    }
    return Array.from(byProj.values());
  }, [rows]);

  const grandTotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.total ?? 0), 0),
    [rows],
  );

  const exportCsv = () => {
    const header = [
      "project_code",
      "cost_code",
      "equipment_code",
      "source",
      "run_hours",
      "ownership",
      "operating",
      "transport",
      "total",
      "is_unposted",
    ];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        r.project_code ?? "",
        r.cost_code ?? "",
        r.equipment_code,
        r.source,
        r.run_hours ?? "",
        r.ownership,
        r.operating,
        r.transport,
        r.total,
        r.is_unposted ? "true" : "false",
      ].map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `equipment-cost-by-job-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!packLoading && !enabled) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/equipment" })}
          >
            Back to Equipment
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Equipment cost by job
          </Typography>
        </Stack>
        <HelpHint
          docPath="equipment-costing/README.md"
          label="Equipment costing help"
        />
      </Stack>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <Box sx={{ minWidth: 320 }}>
          <ProjectIdSelect
            value={projectId}
            label="Project"
            onChange={setProjectId}
            statusFilter={["active"]}
            placeholder="All active projects…"
          />
        </Box>
        <TextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeUnposted}
              onChange={(_, v) => setIncludeUnposted(v)}
            />
          }
          label="Include unposted"
        />
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          Export CSV
        </Button>
      </Stack>

      {reportQ.isLoading && <CircularProgress size={20} />}
      {reportQ.error && <Alert severity="error">Failed to load report.</Alert>}

      {!reportQ.isLoading && rows.length === 0 && (
        <Alert severity="info">
          No equipment cost lines for the selected filters. Try widening the
          date range or enabling "Include unposted".
        </Alert>
      )}

      {grouped.map((pg) => (
        <Paper variant="outlined" sx={{ mb: 3 }} key={pg.projectKey}>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6">{pg.projectLabel}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {fmtMoney(pg.total)}
            </Typography>
          </Box>
          {pg.codeGroups.map((cg) => {
            const codeLabel =
              cg.rows[0]?.cost_code ?? "(no cost code)";
            return (
              <Box key={cg.label}>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: "action.hover",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {codeLabel}
                  </Typography>
                  <Typography variant="body2">
                    {fmtMoney(cg.subtotal)}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Equipment</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Run hrs</TableCell>
                      <TableCell align="right">Ownership</TableCell>
                      <TableCell align="right">Operating</TableCell>
                      <TableCell align="right">Transport</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cg.rows.map((r, i) => {
                      const isRental = r.source === "rental";
                      return (
                        <TableRow key={`${r.equipment_id}-${i}`} hover>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {r.equipment_code}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={isRental ? "Rental" : "Time"}
                              color={isRental ? "info" : "default"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {isRental || r.run_hours == null ? "—" : r.run_hours}
                          </TableCell>
                          <TableCell align="right">
                            {isRental ? "—" : fmtMoney(r.ownership)}
                          </TableCell>
                          <TableCell align="right">
                            {isRental ? "—" : fmtMoney(r.operating)}
                          </TableCell>
                          <TableCell align="right">
                            {isRental ? "—" : fmtMoney(r.transport)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {fmtMoney(r.total)}
                          </TableCell>
                          <TableCell>
                            {r.is_unposted && (
                              <Chip
                                size="small"
                                label="Unposted"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            );
          })}
        </Paper>
      ))}

      {rows.length > 0 && (
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="h6">
            Grand total: {fmtMoney(grandTotal)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
